import { createContext, useContext, useMemo, type ReactNode } from "react";
import type {
  AdBriefAssetsBoolExp,
  AdBriefsBoolExp,
  AdStudioProjectsBoolExp,
} from "src/graphql";
import {
  useMarkStickySearchParamRead,
  useStickySearchParam,
} from "src/pages/ad-studio/sticky-search-params";

const BRAND_PARAM = "brand";

function brandProfileFilters(brandProfileId: string | null) {
  const projectWhere: AdStudioProjectsBoolExp = brandProfileId
    ? { brand_profile_id: { _eq: brandProfileId } }
    : {};
  const briefWhere: AdBriefsBoolExp = brandProfileId
    ? { ad_studio_project: projectWhere }
    : {};
  const assetWhere: AdBriefAssetsBoolExp = brandProfileId
    ? { ad_brief: briefWhere }
    : {};
  return { projectWhere, briefWhere, assetWhere };
}

type BrandProfileFilter = ReturnType<typeof brandProfileFilters> & {
  brandProfileId: string | null;
  setBrandProfileId: (id: string | null) => void;
};

// Views rendered outside AdStudioBrandProfileFilterProvider get the unfiltered default.
const BrandProfileFilterContext = createContext<BrandProfileFilter>({
  ...brandProfileFilters(null),
  brandProfileId: null,
  setBrandProfileId: () => {},
});

/**
 * The brand lives in `?brand=`, so a shared link shows exactly it, and follows
 * you between pages (see `useStickySearchParam`). No brand means every brand.
 */
export function AdStudioBrandProfileFilterProvider({
  children,
}: {
  children: ReactNode;
}) {
  // Every Ad Studio page sits under this provider, so it leaves marking the
  // brand as read to the pages that filter by it.
  const [brandProfileId, setBrandProfileId] = useStickySearchParam(
    BRAND_PARAM,
    null,
    { marksRead: false },
  );

  const value = useMemo(
    () => ({
      ...brandProfileFilters(brandProfileId),
      brandProfileId,
      setBrandProfileId,
    }),
    [brandProfileId, setBrandProfileId],
  );
  return (
    <BrandProfileFilterContext.Provider value={value}>
      {children}
    </BrandProfileFilterContext.Provider>
  );
}

/**
 * Pages that call this keep `?brand=` in their URL; pages that don't drop it.
 * `marksRead` false reads the brand without naming it, for overlays like
 * search that open over pages which don't filter by brand.
 */
export function useAdStudioBrandProfileFilter({
  marksRead = true,
}: { marksRead?: boolean } = {}) {
  useMarkStickySearchParamRead(BRAND_PARAM, marksRead);
  return useContext(BrandProfileFilterContext);
}
